import { Edit3, Calendar, Star, Package, Briefcase, Plus, Eye, MessageSquare, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { useUser } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import BottomNavigation from "@/components/BottomNavigation";

const Profile = () => {
  const [activeTab, setActiveTab] = useState("services");
  const { user } = useUser();
  const navigate = useNavigate();
  const [navigationTab, setNavigationTab] = useState('profiel');

  // Mock user profile data - would come from API/database
  const profileData = {
    name: user?.name || "Professional",
    email: "sarah@example.com",
    avatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Amsterdam, Netherlands",
    joinDate: "January 2024",
    verified: true,
    bio: "Mindfulness coach and wellness enthusiast helping conscious women step into their power through meditation, breathwork, and holistic healing practices.",
    stats: {
      services: 3,
      products: 2,
      appointments: 12,
      reviews: 18,
      rating: 4.9
    }
  };

  // Mock services data
  const myServices = [
    {
      id: 1,
      title: "Mindful Life Coaching Session",
      price: 85,
      duration: "90 min",
      bookings: 8,
      rating: 4.9,
      status: "Active",
      image: "https://images.pexels.com/photos/3760514/pexels-photo-3760514.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      id: 2,
      title: "Breathwork & Meditation Circle",
      price: 35,
      duration: "60 min",
      bookings: 15,
      rating: 4.8,
      status: "Active",
      image: "https://images.pexels.com/photos/3822622/pexels-photo-3822622.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      id: 3,
      title: "Chakra Alignment Session",
      price: 75,
      duration: "75 min",
      bookings: 3,
      rating: 5.0,
      status: "Draft",
      image: "https://images.pexels.com/photos/3820360/pexels-photo-3820360.jpeg?auto=compress&cs=tinysrgb&w=300"
    }
  ];

  // Mock products data
  const myProducts = [
    {
      id: 1,
      title: "Crystal Meditation Set",
      price: 89.99,
      stock: 3,
      sold: 12,
      rating: 4.8,
      status: "Active",
      image: "https://images.pexels.com/photos/6646918/pexels-photo-6646918.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      id: 2,
      title: "Sage Cleansing Bundle",
      price: 24.99,
      stock: 8,
      sold: 25,
      rating: 4.9,
      status: "Active",
      image: "https://images.pexels.com/photos/6192525/pexels-photo-6192525.jpeg?auto=compress&cs=tinysrgb&w=300"
    }
  ];

  // Mock appointments data
  const myAppointments = [
    {
      id: 1,
      clientName: "Emma S.",
      service: "Mindful Life Coaching",
      date: "2024-01-15",
      time: "14:00",
      status: "Upcoming",
      type: "Online"
    },
    {
      id: 2,
      clientName: "Lisa M.",
      service: "Breathwork Session",
      date: "2024-01-12",
      time: "10:00",
      status: "Completed",
      type: "In-person"
    },
    {
      id: 3,
      clientName: "Maya K.",
      service: "Chakra Alignment",
      date: "2024-01-18",
      time: "16:30",
      status: "Upcoming",
      type: "Online"
    }
  ];

  // Mock reviews data
  const myReviews = [
    {
      id: 1,
      clientName: "Anna B.",
      service: "Mindful Life Coaching",
      rating: 5,
      comment: "Sarah's coaching transformed my perspective on life. Her gentle guidance and practical tools helped me find clarity and purpose. Highly recommended!",
      date: "3 days ago"
    },
    {
      id: 2,
      clientName: "Sophie L.",
      service: "Crystal Meditation Set",
      rating: 5,
      comment: "Beautiful crystals with amazing energy! The meditation guide included was so helpful. Perfect for my spiritual practice.",
      date: "1 week ago"
    },
    {
      id: 3,
      clientName: "Rachel D.",
      service: "Breathwork Session",
      rating: 4,
      comment: "Incredibly healing session. Sarah created such a safe, nurturing space. I felt so much lighter afterwards.",
      date: "2 weeks ago"
    }
  ];

  const handleEditProfile = () => {
    console.log("Opening profile edit form");
    // TODO: Navigate to edit profile screen or open modal
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-700";
      case "Draft":
        return "bg-yellow-100 text-yellow-700";
      case "Upcoming":
        return "bg-blue-100 text-blue-700";
      case "Completed":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#f6f1eb' }}>
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-lg" style={{ borderBottom: '1px solid #efe7dd' }}>
        <div className="px-6 py-6">
          {/* Back Button */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="p-2 hover:bg-white/50"
              style={{ color: '#837069' }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: '#837069' }}>Mijn Profiel</h1>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Beheer je WOM profiel en aanbod
              </p>
            </div>
          </div>
          {/* Profile Header */}
          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
            <div className="text-center">
              <div className="mb-2">
                <h1 className="text-3xl font-light tracking-wider" style={{ color: '#837069' }}>
                  <span className="font-bold" style={{ color: '#a0897c' }}>Mijn Profiel</span>
                </h1>
              </div>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Laat zien wie je bent als woman on a mission
              </p>
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex items-start gap-6 mb-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={profileData.avatar} alt={profileData.name} />
              <AvatarFallback style={{ backgroundColor: '#efe7dd', color: '#837069' }} className="text-2xl">
                {profileData.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-bold" style={{ color: '#837069' }}>{profileData.name}</h2>
                {profileData.verified && (
                  <Badge variant="secondary" style={{ backgroundColor: '#efe7dd', color: '#a0897c' }}>
                    Verified
                  </Badge>
                )}
                <div className="text-white px-2 py-1 rounded-full text-xs font-bold tracking-wide" style={{ background: 'linear-gradient(to right, #a0897c, #837069)' }}>
                  WOM
                </div>
              </div>

              <p className="font-medium mb-1" style={{ color: '#a0897c' }}>Woman on a Mission</p>
              <p className="mb-2" style={{ color: '#b6a092' }}>{profileData.location}</p>
              <p className="text-sm mb-3" style={{ color: '#b6a092' }}>Member since {profileData.joinDate}</p>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium" style={{ color: '#837069' }}>{profileData.stats.rating}</span>
                  <span style={{ color: '#b6a092' }}>({profileData.stats.reviews} reviews)</span>
                </div>
              </div>
            </div>

            <Button 
              onClick={handleEditProfile}
              variant="outline"
              className="flex items-center gap-2 hover:bg-white/50"
              style={{ borderColor: '#efe7dd', color: '#837069' }}
            >
              <Edit3 className="h-4 w-4" />
              Edit profile
            </Button>
          </div>

          {/* Bio */}
          <p className="leading-relaxed mb-6 max-w-2xl" style={{ color: '#837069' }}>
            {profileData.bio}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#837069' }}>{profileData.stats.services}</div>
              <div className="text-sm" style={{ color: '#b6a092' }}>Services</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#837069' }}>{profileData.stats.products}</div>
              <div className="text-sm" style={{ color: '#b6a092' }}>Products</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#837069' }}>{profileData.stats.appointments}</div>
              <div className="text-sm" style={{ color: '#b6a092' }}>Appointments</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#837069' }}>{profileData.stats.reviews}</div>
              <div className="text-sm" style={{ color: '#b6a092' }}>Reviews</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Content */}
      <div className="px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6" style={{ backgroundColor: '#efe7dd' }}>
            <TabsTrigger value="services" className="flex items-center gap-2 data-[state=active]:bg-white" style={{ color: '#837069' }}>
              <Briefcase className="h-4 w-4" />
              <span className="hidden sm:inline">My services</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2 data-[state=active]:bg-white" style={{ color: '#837069' }}>
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">My products</span>
            </TabsTrigger>
            <TabsTrigger value="appointments" className="flex items-center gap-2 data-[state=active]:bg-white" style={{ color: '#837069' }}>
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">My appointments</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center gap-2 data-[state=active]:bg-white" style={{ color: '#837069' }}>
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Reviews</span>
            </TabsTrigger>
          </TabsList>

          {/* My Services Tab */}
          <TabsContent value="services">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold" style={{ color: '#837069' }}>My Services</h2>
                <Button style={{ backgroundColor: '#a0897c', color: 'white' }} className="hover:opacity-90">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service
                </Button>
              </div>
              {myServices.map((service) => (
                <Card key={service.id} className="shadow-sm bg-white/80 backdrop-blur-sm" style={{ border: '1px solid #efe7dd' }}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold" style={{ color: '#837069' }}>{service.title}</h3>
                          <Badge className={getStatusColor(service.status)}>
                            {service.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm mb-2" style={{ color: '#b6a092' }}>
                          <span>€{service.price}</span>
                          <span>•</span>
                          <span>{service.duration}</span>
                          <span>•</span>
                          <span>{service.bookings} bookings</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm" style={{ color: '#b6a092' }}>{service.rating}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="hover:bg-white/50" style={{ color: '#837069' }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* My Products Tab */}
          <TabsContent value="products">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold" style={{ color: '#837069' }}>My Products</h2>
                <Button style={{ backgroundColor: '#a0897c', color: 'white' }} className="hover:opacity-90">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>
              {myProducts.map((product) => (
                <Card key={product.id} className="shadow-sm bg-white/80 backdrop-blur-sm" style={{ border: '1px solid #efe7dd' }}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold" style={{ color: '#837069' }}>{product.title}</h3>
                          <Badge className={getStatusColor(product.status)}>
                            {product.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm mb-2" style={{ color: '#b6a092' }}>
                          <span>€{product.price}</span>
                          <span>•</span>
                          <span>{product.stock} in stock</span>
                          <span>•</span>
                          <span>{product.sold} sold</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm" style={{ color: '#b6a092' }}>{product.rating}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="hover:bg-white/50" style={{ color: '#837069' }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* My Appointments Tab */}
          <TabsContent value="appointments">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold" style={{ color: '#837069' }}>My Appointments</h2>
              {myAppointments.map((appointment) => (
                <Card key={appointment.id} className="shadow-sm bg-white/80 backdrop-blur-sm" style={{ border: '1px solid #efe7dd' }}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold" style={{ color: '#837069' }}>{appointment.service}</h3>
                          <Badge className={getStatusColor(appointment.status)}>
                            {appointment.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm" style={{ color: '#b6a092' }}>
                          <span>with {appointment.clientName}</span>
                          <span>•</span>
                          <span>{new Date(appointment.date).toLocaleDateString()} at {appointment.time}</span>
                          <span>•</span>
                          <span>{appointment.type}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="hover:bg-white/50" style={{ color: '#837069' }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold" style={{ color: '#837069' }}>Reviews</h2>
              {myReviews.map((review) => (
                <Card key={review.id} className="shadow-sm bg-white/80 backdrop-blur-sm" style={{ border: '1px solid #efe7dd' }}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium" style={{ color: '#837069' }}>{review.clientName}</span>
                          <span className="text-sm" style={{ color: '#b6a092' }}>•</span>
                          <span className="text-sm" style={{ color: '#b6a092' }}>{review.service}</span>
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-4 w-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <span className="text-sm" style={{ color: '#b6a092' }}>{review.date}</span>
                    </div>
                    <p className="leading-relaxed" style={{ color: '#837069' }}>{review.comment}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation 
        activeTab={navigationTab} 
        onTabChange={(tab) => {
          setNavigationTab(tab);
          if (tab === 'home') {
            navigate('/');
          } else if (tab === 'marketplace') {
            navigate('/marketplace');
          } else if (tab === 'berichten') {
            navigate('/messages');
          } else if (tab === 'profiel') {
            // Already on profile page
          }
        }} 
      />
    </div>
  );
};

export default Profile;